/* =========================================================
   BOOKING — the on-page time picker and the #book panel.
   Markup: snippets/booking-dialog.liquid (panel + settings JSON)
   and main-product.liquid (picker on services whose delivery
   type is Booking).

   Slots come from the opening hours in Theme settings → Booking,
   in the studio's fixed UTC offset, minus "blocked" lines and
   minus anything busy in Google Calendar (free/busy API, only
   when a calendar ID and API key are set). Nothing here reserves
   a slot: the free call arrives as a contact message, a paid
   session as an order, and the studio confirms either one.
   ========================================================= */
(function () {
  'use strict';

  // Words the visitor reads come from window.BRS.t (layout/theme.liquid), in their
  // language; the English here is only the fallback. {name} slots are filled from o.
  function T(k, f, o) {
    var d = window.BRS && window.BRS.t, s = (d && d[k]) || f;
    return o ? s.replace(/\{(\w+)\}/g, function (m, n) { return n in o ? o[n] : m; }) : s;
  }

  var CFG = {};
  try { CFG = JSON.parse(document.getElementById('booking-config').textContent) || {}; } catch (e) {}
  var MIN = 6e4, HOUR = 36e5, DAY = 864e5;
  var OFF = (parseFloat(CFG.offset) || 0) * HOUR;
  var LANG = document.documentElement.lang || 'en';

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function clock(mins) { return pad(Math.floor(mins / 60)) + ':' + pad(mins % 60); }

  // "10:00-13:00, 14:00-18:00" -> [[600, 780], [840, 1080]] (minutes from midnight)
  function ranges(txt) {
    return String(txt || '').split(/[,;]/).map(function (r) {
      var m = r.match(/(\d{1,2})(?:[:h.](\d{2}))?\s*[-–]\s*(\d{1,2})(?:[:h.](\d{2}))?/);
      if (!m) return null;
      var a = +m[1] * 60 + +(m[2] || 0), b = +m[3] * 60 + +(m[4] || 0);
      return b > a ? [a, b] : null;
    }).filter(Boolean);
  }
  var HOURS = (CFG.hours || []).map(ranges); // index 0 = Sunday

  // Studio-local date + minutes -> UTC milliseconds.
  function at(y, mo, d, mins) { return Date.UTC(y, mo, d) - OFF + (mins || 0) * MIN; }

  // "2026-12-24" or "2026-10-02 14:00-16:00", in studio time.
  var BLOCKED = (CFG.blocked || []).map(function (line) {
    var m = String(line).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2}))?/);
    if (!m) return null;
    var y = +m[1], mo = +m[2] - 1, d = +m[3];
    if (!m[4]) return { from: at(y, mo, d), to: at(y, mo, d) + DAY };
    return { from: at(y, mo, d, +m[4] * 60 + +m[5]), to: at(y, mo, d, +m[6] * 60 + +m[7]) };
  }).filter(Boolean);

  function clash(a, b, list) {
    for (var i = 0; i < list.length; i++) if (a < list[i].to && b > list[i].from) return true;
    return false;
  }

  // Google Calendar free/busy. Needs the calendar shared publicly as
  // "free/busy only" and a browser API key; any failure falls back to hours.
  var busyP = null;
  function loadBusy() {
    if (busyP) return busyP;
    if (!CFG.gcal || !CFG.key) return (busyP = Promise.resolve([]));
    var now = Date.now(), ahead = Math.max(+CFG.days || 30, +CFG.dates || 180);
    busyP = fetch('https://www.googleapis.com/calendar/v3/freeBusy?key=' + encodeURIComponent(CFG.key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        timeMin: new Date(now).toISOString(),
        timeMax: new Date(now + (ahead + 1) * DAY).toISOString(),
        items: [{ id: CFG.gcal }]
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var c = j && j.calendars && j.calendars[CFG.gcal];
        if (!c || c.errors) {
          console.warn('[booking] Google Calendar not readable; showing opening hours only.', (c && c.errors) || (j && j.error));
          return [];
        }
        return (c.busy || []).map(function (b) { return { from: Date.parse(b.start), to: Date.parse(b.end) }; });
      })
      .catch(function () { return []; });
    return busyP;
  }

  function slotsFor(day, minutes, step, busy) {
    var earliest = Date.now() + (+CFG.notice || 0) * HOUR;
    var dow = new Date(Date.UTC(day.y, day.m, day.d)).getUTCDay();
    var out = [];
    (HOURS[dow] || []).forEach(function (r) {
      for (var t = r[0]; t + minutes <= r[1]; t += step) {
        var from = at(day.y, day.m, day.d, t), to = from + minutes * MIN;
        if (from < earliest || clash(from, to, BLOCKED) || clash(from, to, busy)) continue;
        out.push({ from: from, to: to, start: t, end: t + minutes });
      }
    });
    return out;
  }

  // Date mode (shoots, events): any day past the notice, unless a day off or an
  // all-day busy block covers it. Returns the day as one all-day "slot".
  function daySlot(day, busy, firstOpen) {
    var from = at(day.y, day.m, day.d), to = from + DAY;
    if (from < firstOpen) return [];
    var covered = function (list) { return list.some(function (x) { return x.from <= from && x.to >= to; }); };
    return covered(BLOCKED) || covered(busy) ? [] : [{ from: from, to: to, allDay: true }];
  }

  // ---- Labels ----
  var fmtDay = new Intl.DateTimeFormat(LANG, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  var fmtLong = new Intl.DateTimeFormat(LANG, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  var fmtMonth = new Intl.DateTimeFormat(LANG, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  var fmtLocal = new Intl.DateTimeFormat(LANG, { hour: '2-digit', minute: '2-digit' });
  function dayDate(day) { return new Date(Date.UTC(day.y, day.m, day.d)); }
  function sameZone(ms) { return -new Date(ms).getTimezoneOffset() * MIN === OFF; }
  function slotText(day, s) {
    if (s.allDay) return fmtLong.format(dayDate(day));
    return fmtLong.format(dayDate(day)) + ', ' + clock(s.start) + '–' + clock(s.end) + ' (' + (CFG.tz || T('bk_tz_studio', 'studio time')) + ')';
  }
  function gcalDate(ms) { return new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, ''); }
  // All-day slots are written as studio dates (YYYYMMDD/next day), as Google expects.
  function gcalDay(ms) { return new Date(ms + OFF).toISOString().slice(0, 10).replace(/-/g, ''); }
  function gcalLink(title, s, details) {
    return 'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      '&text=' + encodeURIComponent(title) +
      '&dates=' + (s.allDay ? gcalDay(s.from) + '/' + gcalDay(s.to) : gcalDate(s.from) + '/' + gcalDate(s.to)) +
      '&details=' + encodeURIComponent(details || '') +
      '&location=' + encodeURIComponent(CFG.studio || '');
  }

  /* ---------- Picker ----------
     A strip of seven days (paged with ‹ ›) and the free times of the
     chosen day, like a Google Calendar booking page. opts.onPick gets
     { day, slot, text } or null when the choice is cleared. */
  function Picker(el, opts) {
    this.el = el;
    this.mode = opts.mode === 'day' ? 'day' : 'time';
    this.minutes = +opts.minutes || 60;
    this.step = +opts.step || this.minutes;
    this.onPick = opts.onPick || function () {};
    var s = new Date(Date.now() + OFF);
    this.today = { y: s.getUTCFullYear(), m: s.getUTCMonth(), d: s.getUTCDate() };
    this.days = this.mode === 'day' ? (+CFG.dates || 180) : (+CFG.days || 30);
    this.page = 0;
    this.sel = null;     // { day, slot }
    this.dayIdx = null;  // index into this.list
    this.busy = null;
    el.innerHTML =
      '<div class="bk">' +
        '<div class="bk__top">' +
          '<button type="button" class="bk__nav" data-bk-prev aria-label="' + T('bk_earlier', 'Earlier days') + '">‹</button>' +
          '<span class="bk__month" data-bk-month></span>' +
          '<button type="button" class="bk__nav" data-bk-next aria-label="' + T('bk_later', 'Later days') + '">›</button>' +
        '</div>' +
        '<div class="bk__days" data-bk-days></div>' +
        '<div class="bk__slots" data-bk-slots aria-live="polite"><p class="bk__empty">' + T('bk_checking', 'Checking availability…') + '</p></div>' +
        '<p class="bk__tz" data-bk-tz></p>' +
      '</div>';
    var self = this;
    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-bk-prev]')) { self.page--; self.renderDays(); return; }
      if (e.target.closest('[data-bk-next]')) { self.page++; self.renderDays(); return; }
      var d = e.target.closest('[data-bk-day]');
      if (d && !d.disabled) {
        self.dayIdx = +d.getAttribute('data-bk-day');
        if (self.mode === 'day') {
          // In date mode the day is the choice.
          var picked = self.list[self.dayIdx];
          self.sel = { day: picked, slot: picked.slots[0] };
          self.onPick({ day: picked, slot: picked.slots[0], text: slotText(picked, picked.slots[0]) });
        }
        self.renderDays();
        return;
      }
      var t = e.target.closest('[data-bk-slot]');
      if (t) {
        var day = self.list[self.dayIdx], slot = day.slots[+t.getAttribute('data-bk-slot')];
        self.sel = { day: day, slot: slot };
        self.renderSlots();
        self.onPick({ day: day, slot: slot, text: slotText(day, slot) });
      }
    });
    loadBusy().then(function (busy) { self.busy = busy; self.build(); });
  }

  Picker.prototype.build = function () {
    this.list = [];
    var firstOpen = at(this.today.y, this.today.m, this.today.d) + (+CFG.dnotice || 0) * DAY;
    for (var i = 0; i < this.days; i++) {
      var dt = new Date(Date.UTC(this.today.y, this.today.m, this.today.d + i));
      var day = { y: dt.getUTCFullYear(), m: dt.getUTCMonth(), d: dt.getUTCDate() };
      day.slots = this.mode === 'day'
        ? daySlot(day, this.busy || [], firstOpen)
        : slotsFor(day, this.minutes, this.step, this.busy || []);
      this.list.push(day);
    }
    // Keep the chosen slot if it still fits (e.g. after a variant change), else clear it.
    if (this.sel) {
      var keep = null, from = this.sel.slot.from;
      this.list.some(function (day, i) {
        return day.slots.some(function (s) { if (s.from === from) { keep = { day: day, slot: s, i: i }; return true; } return false; });
      });
      if (keep) { this.sel = { day: keep.day, slot: keep.slot }; this.dayIdx = keep.i; this.onPick({ day: keep.day, slot: keep.slot, text: slotText(keep.day, keep.slot) }); }
      else { this.sel = null; this.onPick(null); }
    }
    var firstFree = null;
    for (var j = 0; j < this.list.length; j++) if (this.list[j].slots.length) { firstFree = j; break; }
    if (this.mode === 'day') {
      // Nothing is pre-selected in date mode: a highlighted day would look already chosen.
      if (!this.sel) this.dayIdx = null;
      this.page = Math.floor((this.dayIdx != null ? this.dayIdx : firstFree || 0) / 7);
    } else {
      if (this.dayIdx == null || !this.list[this.dayIdx] || !this.list[this.dayIdx].slots.length) this.dayIdx = firstFree;
      this.page = this.dayIdx == null ? 0 : Math.floor(this.dayIdx / 7);
    }
    var tz = this.el.querySelector('[data-bk-tz]');
    var h = OFF / HOUR;
    tz.textContent = this.mode === 'day'
      ? T('bk_day_note', 'We’ll agree the timing of the day with you.')
      : T('bk_tz', 'Times are in {tz} (UTC{off})', { tz: CFG.tz || T('bk_tz_studio', 'studio time'), off: (h >= 0 ? '+' : '−') + Math.abs(h) }) +
        (sameZone(Date.now()) ? '.' : T('bk_yours', '; yours are in brackets.'));
    this.renderDays();
  };

  Picker.prototype.setMinutes = function (minutes) {
    minutes = +minutes || 60;
    if (minutes === this.minutes) return;
    this.minutes = minutes;
    if (this.busy) this.build();
  };

  Picker.prototype.renderDays = function () {
    var pages = Math.max(1, Math.ceil(this.list.length / 7));
    this.page = Math.min(Math.max(this.page, 0), pages - 1);
    var start = this.page * 7, html = '';
    for (var i = start; i < Math.min(start + 7, this.list.length); i++) {
      var day = this.list[i], parts = fmtDay.formatToParts(dayDate(day));
      var wd = '', dn = '';
      parts.forEach(function (p) { if (p.type === 'weekday') wd = p.value; if (p.type === 'day') dn = p.value; });
      html += '<button type="button" class="bk__day' + (i === this.dayIdx ? ' is-active' : '') + '" data-bk-day="' + i + '"' +
        (day.slots.length ? '' : ' disabled') + ' aria-pressed="' + (i === this.dayIdx) + '" aria-label="' + fmtLong.format(dayDate(day)) + '">' +
        '<span class="bk__wd">' + wd + '</span><span class="bk__dn">' + dn + '</span></button>';
    }
    this.el.querySelector('[data-bk-days]').innerHTML = html;
    var first = this.list[start];
    this.el.querySelector('[data-bk-month]').textContent = first ? fmtMonth.format(dayDate(first)) : '';
    this.el.querySelector('[data-bk-prev]').disabled = this.page === 0;
    this.el.querySelector('[data-bk-next]').disabled = this.page >= pages - 1;
    this.renderSlots();
  };

  Picker.prototype.renderSlots = function () {
    var box = this.el.querySelector('[data-bk-slots]');
    if (this.mode === 'day') {
      box.innerHTML = this.sel ? '' : '<p class="bk__empty">' + T('bk_pick_day', 'Pick the day of your shoot or event.') + '</p>';
      return;
    }
    var day = this.dayIdx == null ? null : this.list[this.dayIdx];
    if (!day) { box.innerHTML = '<p class="bk__empty">' + T('bk_none', 'No free times in the next {n} days. Message us and we’ll find one.', { n: this.days }) + '</p>'; return; }
    var sel = this.sel, html = '';
    day.slots.forEach(function (s, i) {
      var on = sel && sel.slot.from === s.from;
      html += '<button type="button" class="bk__slot' + (on ? ' is-active' : '') + '" data-bk-slot="' + i + '" aria-pressed="' + !!on + '">' +
        clock(s.start) + (sameZone(s.from) ? '' : ' <small>(' + fmtLocal.format(new Date(s.from)) + ')</small>') + '</button>';
    });
    box.innerHTML = '<p class="bk__daylabel">' + fmtLong.format(dayDate(day)) + '</p><div class="bk__grid">' + html + '</div>';
  };

  /* ---------- Product page: book and pay in one go ---------- */
  // A variant's custom.booking_minutes (data-booking-minutes), else a length in its
  // title ("Half day (4 h)", "90 min"), else the product's value.
  function minutesFor(opt, fallback) {
    if (opt && +opt.getAttribute('data-booking-minutes')) return +opt.getAttribute('data-booking-minutes');
    var t = String((opt && opt.getAttribute('data-title')) || ''), m;
    if ((m = t.match(/(\d+(?:[.,]\d+)?)\s*(?:h\b|hr|hour|heure)/i))) return Math.round(parseFloat(m[1].replace(',', '.')) * 60);
    if ((m = t.match(/(\d+)\s*-?\s*min/i))) return +m[1];
    return fallback;
  }

  function initProduct() {
    document.querySelectorAll('[data-booking-product]').forEach(function (wrap) {
      var form = wrap.closest('form');
      var el = wrap.querySelector('[data-booking-picker]');
      var chosen = wrap.querySelector('[data-booking-chosen]');
      var base = +el.getAttribute('data-minutes') || 60;
      var select = form && form.querySelector('#variantSelect');
      var currentOpt = function () { return select ? select.selectedOptions[0] : null; };
      var currentTitle = function () { var o = currentOpt(); return o ? o.getAttribute('data-title') : ''; };
      var field = function (n) { return wrap.querySelector('[data-booking-field="' + n + '"]'); };
      var title = wrap.getAttribute('data-product-title') || document.title;

      var picker = new Picker(el, {
        mode: wrap.getAttribute('data-mode'),
        minutes: minutesFor(currentOpt(), base),
        step: el.getAttribute('data-step'),
        onPick: function (p) {
          field('label').value = p ? p.text : '';
          field('start').value = !p ? '' : p.slot.allDay ? new Date(p.slot.from + OFF).toISOString().slice(0, 10) : new Date(p.slot.from).toISOString();
          field('gcal').value = p ? gcalLink(title + (currentTitle() ? ' — ' + currentTitle() : ''), p.slot, 'Booked and paid on the website. Client details are on the Shopify order.') : '';
          chosen.textContent = p ? p.text : '';
          chosen.hidden = !p;
          if (p) wrap.classList.remove('is-missing');
        }
      });
      // The theme swaps #variantSelect on the option radios' change; follow it.
      if (form) form.addEventListener('change', function (e) {
        if (e.target.matches('[data-option-position]')) picker.setMinutes(minutesFor(currentOpt(), base));
      });
    });

    // No slot, no add to cart. Capture phase, so it runs before theme.js's AJAX submit.
    document.addEventListener('submit', function (e) {
      var wrap = e.target.querySelector && e.target.querySelector('[data-booking-product]');
      if (!wrap || wrap.querySelector('[data-booking-field="label"]').value) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      wrap.classList.add('is-missing');
      var err = e.target.querySelector('[data-form-error]');
      if (err) err.textContent = wrap.getAttribute('data-mode') === 'day' ? T('bk_need_date', 'Pick a date first.') : T('bk_need_slot', 'Pick a date and time first.');
      wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);
  }

  /* ---------- The #book panel ---------- */
  function embedUrls(raw) {
    var m = (raw || '').match(/src\s*=\s*["']([^"']+)["']/i);
    var u = (m ? m[1] : raw || '').trim();
    if (!/^https:\/\//i.test(u)) return null;
    var embed = u;
    // Zoho only lets its booking page be framed under /portal-embed.
    if (/zohobookings\./i.test(u) && u.indexOf('/portal-embed') === -1) {
      var i = u.indexOf('#');
      embed = (i === -1 ? u : u.slice(0, i)).replace(/\/+$/, '') + '/portal-embed' + (i === -1 ? '#/' : u.slice(i));
    }
    return { embed: embed, page: embed.replace('/portal-embed', '/') };
  }

  function initPanel() {
    var dlg = document.getElementById('booking');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    var urls = embedUrls(dlg.getAttribute('data-src'));
    var frame = dlg.querySelector('[data-booking-frame]');
    var native = dlg.querySelector('[data-booking-native]');
    var picker = null, pick = null;

    function step(name) {
      native.querySelectorAll('[data-step]').forEach(function (s) { s.hidden = s.getAttribute('data-step') !== name; });
    }

    if (urls) {
      dlg.classList.add('is-embed');
      frame.hidden = false;
      native.hidden = true;
      var tab = dlg.querySelector('[data-booking-newtab]');
      tab.href = urls.page;
      tab.hidden = false;
    }

    function done(p) {
      var text = dlg.querySelector('[data-booking-done-text]');
      text.textContent = T('bk_done', 'We’ve got your request for {when}. We’ll confirm by email{wa}.', { when: p.text, wa: CFG.whatsapp ? T('bk_or_wa', ' or WhatsApp') : '' });
      var add = dlg.querySelector('[data-booking-add-gcal]');
      add.href = gcalLink(p.name + ' — ' + (CFG.studio || ''), { from: p.from, to: p.to }, T('bk_gcal_note', 'Requested on the website. The studio will confirm.'));
      var wa = dlg.querySelector('[data-booking-wa]');
      if (wa) wa.href = 'https://wa.me/' + CFG.whatsapp + '?text=' + encodeURIComponent(T('bk_wa_msg', 'Hello! I just requested a {name} for {when}.', { name: p.name, when: p.text }));
      step('done');
    }

    function open() {
      if (urls) {
        if (!frame.querySelector('iframe')) {
          var f = document.createElement('iframe');
          f.src = urls.embed;
          f.title = dlg.querySelector('h2').textContent;
          f.setAttribute('allow', 'payment');
          f.addEventListener('load', function () { frame.classList.add('is-loaded'); });
          frame.appendChild(f);
        }
      } else if (!picker) {
        var el = native.querySelector('[data-booking-picker]');
        picker = new Picker(el, {
          minutes: el.getAttribute('data-minutes'),
          step: el.getAttribute('data-step'),
          onPick: function (p) {
            if (!p) return;
            pick = p;
            native.querySelector('[data-booking-chosen]').textContent = p.text;
            step('details');
            var first = native.querySelector('[data-step="details"] input:not([type=hidden])');
            if (first) first.focus();
          }
        });
      }
      if (!dlg.open) dlg.showModal();
      document.body.style.overflow = 'hidden';
    }

    dlg.addEventListener('close', function () {
      document.body.style.overflow = '';
      if (location.hash === '#book') history.replaceState(null, '', location.pathname + location.search);
    });
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg || e.target.closest('[data-booking-close]')) { dlg.close(); return; }
      if (e.target.closest('[data-booking-back]')) { step('pick'); return; }
      // A paid-service shortcut for the page we're already on: just go to its picker.
      var chip = e.target.closest('a.booking__chip[href]');
      if (chip && chip.pathname === location.pathname) {
        e.preventDefault();
        dlg.close();
        var target = document.getElementById('choose-time');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    document.addEventListener('click', function (e) {
      var t = e.target.closest('a[href$="#book"], [data-booking-open]');
      if (!t) return;
      e.preventDefault();
      open();
    });

    // The free call goes through Shopify's contact form, so it lands in the store inbox.
    var form = native.querySelector('form');
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!pick) { step('pick'); return; }
      var name = native.getAttribute('data-call-name') || 'call';
      var who = (form.querySelector('[name="contact[name]"]').value || '').trim();
      var set = function (n, v) { var f = form.querySelector('[data-booking-field="' + n + '"]'); if (f) f.value = v; };
      set('label', pick.text);
      set('gcal', gcalLink(name + ' — ' + who, pick.slot,
        'Email: ' + form.querySelector('[name="contact[email]"]').value +
        '\nPhone: ' + (form.querySelector('[name="contact[Phone]"]').value || '—') +
        '\n\n' + (form.querySelector('[name="contact[body]"]').value || '')));
      var btn = form.querySelector('[type=submit]');
      var err = form.querySelector('[data-booking-error]');
      var state = { name: name, text: pick.text, from: pick.slot.from, to: pick.slot.to };
      err.textContent = '';
      btn.disabled = true;
      fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } })
        .then(function (r) {
          if (/\/challenge/.test(r.url)) {
            // Shopify wants a spam check: post normally and reopen on "done" when the page comes back.
            try { sessionStorage.setItem('brs-booking-done', JSON.stringify(state)); } catch (x) {}
            HTMLFormElement.prototype.submit.call(form);
            return;
          }
          if (r.ok && /contact_posted=true/.test(r.url)) done(state);
          else err.textContent = T('bk_err_email', 'That didn’t go through. Check your email address and try again.');
        })
        .catch(function () { err.textContent = T('bk_err_net', 'That didn’t go through. Check your connection and try again.'); })
        .finally(function () { btn.disabled = false; });
    });

    var back = null;
    try { back = JSON.parse(sessionStorage.getItem('brs-booking-done') || 'null'); sessionStorage.removeItem('brs-booking-done'); } catch (x) {}
    if (back && /contact_posted=true/.test(location.search)) { open(); done(back); }
    else if (location.hash === '#book') open();

    window.BRSBooking = { open: open };
  }

  function init() { initPanel(); initProduct(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
